import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Step,
  StepButton,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountType,
  ActivityVolume,
  DemoPersonaDTO,
  getDemoPersonas,
  createDemoAccount,
  triggerGeneration,
  CreateDemoAccountRequest,
} from './DemoEngineService';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const STEPS = ['Account type', 'Select persona', 'Volume & coverage', 'Review'];

const ACCOUNT_TYPE_META: Record<AccountType, { label: string; description: string }> = {
  PERSONAL: {
    label: 'Personal',
    description: 'Individual user — salary, everyday spending, savings goals.',
  },
  FAMILY: {
    label: 'Family',
    description: 'Household with two users — shared bills, childcare, joint goals.',
  },
  BUSINESS: {
    label: 'Business',
    description: 'Small business — revenue, expenses, supplier payments.',
  },
};

const CreateDemoAccountDialog = ({ open, onClose, onCreated }: Props): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('PERSONAL');
  const [personaId, setPersonaId] = useState<number | ''>('');
  const [volume, setVolume] = useState<ActivityVolume>('MEDIUM');
  const [historicalMonths, setHistoricalMonths] = useState<3 | 6 | 12>(6);
  const [futureMonths, setFutureMonths] = useState<3 | 6 | 12>(3);
  const [generateRecurring, setGenerateRecurring] = useState(true);
  const [generateAdHoc, setGenerateAdHoc] = useState(true);
  const [generateGoals, setGenerateGoals] = useState(true);
  const [generateCoachInsights, setGenerateCoachInsights] = useState(true);
  const [generateNow, setGenerateNow] = useState(true);

  const [personas, setPersonas] = useState<DemoPersonaDTO[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      getDemoPersonas(true, accountType)
        .then((res) => setPersonas(res.data))
        .catch(() => {});
    }
  }, [open, accountType]);

  const selectedPersona = personas.find((p) => p.id === personaId);

  // Apply persona defaults when a persona is chosen
  const handlePersonaSelect = (id: number | '') => {
    setPersonaId(id);
    if (id !== '') {
      const p = personas.find((x) => x.id === id);
      if (p) {
        setVolume(p.defaultVolume);
        const histM = [3, 6, 12].includes(p.defaultHistoricalMonths)
          ? (p.defaultHistoricalMonths as 3 | 6 | 12)
          : 6;
        const futM = [3, 6, 12].includes(p.defaultFutureMonths)
          ? (p.defaultFutureMonths as 3 | 6 | 12)
          : 3;
        setHistoricalMonths(histM);
        setFutureMonths(futM);
      }
    }
  };

  const handleNext = () => {
    if (step === 0 && !name.trim()) { setError('Please enter a demo account name.'); return; }
    setError('');
    setStep((s) => s + 1);
  };

  const handleBack = () => { setError(''); setStep((s) => s - 1); };

  const handleStepClick = (i: number) => {
    if (i < step) { setError(''); setStep(i); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    const payload: CreateDemoAccountRequest = {
      name: name.trim(),
      accountType,
      personaId: personaId !== '' ? personaId : undefined,
      activityVolume: volume,
      historicalMonths,
      futureMonths,
      generateRecurring,
      generateAdHoc,
      generateGoals,
      generateCoachInsights,
      enableBehaviourSimulation: false,
    };
    try {
      const res = await createDemoAccount(payload);
      const newId = res.data.id;
      if (generateNow) {
        try { await triggerGeneration(newId); } catch { /* non-fatal — user can trigger from detail page */ }
      }
      onCreated();
      navigate(`/admin/demo-engine/accounts/${newId}`);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Could not create demo account.');
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setName('');
    setAccountType('PERSONAL');
    setPersonaId('');
    setVolume('MEDIUM');
    setHistoricalMonths(6);
    setFutureMonths(3);
    setGenerateNow(true);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Demo Account</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, pt: 1 }}>
          {STEPS.map((label, i) => (
            <Step key={label} completed={i < step}>
              <StepButton onClick={() => handleStepClick(i)} sx={{ borderRadius: 1 }}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 0 — Account type + name */}
        {step === 0 && (
          <Box>
            <TextField
              label="Demo account name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              placeholder="e.g. Young Professional Demo"
              autoFocus
            />
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Account type</Typography>
            <Grid container spacing={1.5}>
              {(Object.keys(ACCOUNT_TYPE_META) as AccountType[]).map((t) => {
                const meta = ACCOUNT_TYPE_META[t];
                const selected = accountType === t;
                return (
                  <Grid item xs={12} sm={4} key={t}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: selected ? theme.palette.primary.main : 'divider',
                        borderWidth: selected ? 2 : 1,
                        transition: 'border-color 0.15s',
                      }}
                    >
                      <CardActionArea
                        onClick={() => { setAccountType(t); setPersonaId(''); }}
                        sx={{ p: 1.5 }}
                      >
                        <Typography variant="body2" fontWeight={700} gutterBottom>
                          {meta.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {meta.description}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Step 1 — Persona */}
        {step === 1 && (
          <Box>
            {personas.length === 0 ? (
              <Alert severity="info">
                No personas available for {accountType.charAt(0) + accountType.slice(1).toLowerCase()} accounts.
                You can proceed without one or add personas from the Personas page.
              </Alert>
            ) : (
              <Grid container spacing={1.5}>
                {/* No persona option */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: personaId === '' ? theme.palette.primary.main : 'divider',
                      borderWidth: personaId === '' ? 2 : 1,
                    }}
                  >
                    <CardActionArea onClick={() => setPersonaId('')} sx={{ p: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>No persona</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use generic defaults — no persona-specific income or spending patterns.
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
                {personas.map((p) => {
                  const selected = personaId === p.id;
                  return (
                    <Grid item xs={12} key={p.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: selected ? theme.palette.primary.main : 'divider',
                          borderWidth: selected ? 2 : 1,
                          transition: 'border-color 0.15s',
                        }}
                      >
                        <CardActionArea onClick={() => handlePersonaSelect(p.id)} sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{p.name}</Typography>
                              {p.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  {p.description}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 1 }}>
                              {p.location && (
                                <Chip label={p.location} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                              )}
                              {p.income != null && (
                                <Chip
                                  label={`£${(p.income / 1000).toFixed(0)}k`}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark }}
                                />
                              )}
                              <Chip
                                label={p.defaultVolume.charAt(0) + p.defaultVolume.slice(1).toLowerCase()}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </Box>
                          </Box>
                          {selected && p.income != null && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Defaults applied: {p.defaultVolume.toLowerCase()} volume ·{' '}
                              {p.defaultHistoricalMonths}m history · {p.defaultFutureMonths}m forecast
                            </Typography>
                          )}
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* Step 2 — Volume & coverage */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Activity volume</Typography>
              <ToggleButtonGroup
                exclusive
                value={volume}
                onChange={(_, v) => { if (v) setVolume(v); }}
                sx={{ width: '100%' }}
              >
                {(['LOW', 'MEDIUM', 'HIGH'] as ActivityVolume[]).map((v) => (
                  <ToggleButton key={v} value={v} sx={{ flex: 1 }}>
                    {v.charAt(0) + v.slice(1).toLowerCase()}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Controls transaction quantity and complexity, not income level.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Historical coverage</Typography>
              <ToggleButtonGroup
                exclusive
                value={historicalMonths}
                onChange={(_, v) => { if (v) setHistoricalMonths(v); }}
              >
                {([3, 6, 12] as const).map((m) => (
                  <ToggleButton key={m} value={m}>{m} months</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Future forecast coverage</Typography>
              <ToggleButtonGroup
                exclusive
                value={futureMonths}
                onChange={(_, v) => { if (v) setFutureMonths(v); }}
              >
                {([3, 6, 12] as const).map((m) => (
                  <ToggleButton key={m} value={m}>{m} months</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Generate</Typography>
              {[
                ['Recurring payments',    generateRecurring,      setGenerateRecurring     ] as const,
                ['Ad hoc payments',       generateAdHoc,          setGenerateAdHoc         ] as const,
                ['Goals & contributions', generateGoals,          setGenerateGoals         ] as const,
                ['Money Coach insights',  generateCoachInsights,  setGenerateCoachInsights ] as const,
              ].map(([label, val, setter]) => (
                <FormControlLabel
                  key={label}
                  control={<Switch checked={val} onChange={(e) => setter(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">{label}</Typography>}
                  sx={{ display: 'flex', ml: 0 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Step 3 — Review */}
        {step === 3 && (
          <Box sx={{ p: 0.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Summary</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                mb: 2,
              }}
            >
              {[
                ['Name',               name],
                ['Type',               accountType.charAt(0) + accountType.slice(1).toLowerCase()],
                ['Persona',            selectedPersona?.name ?? 'None'],
                ['Volume',             volume.charAt(0) + volume.slice(1).toLowerCase()],
                ['Historical',         `${historicalMonths} months`],
                ['Future',             `${futureMonths} months`],
                ['Recurring payments', generateRecurring ? 'Yes' : 'No'],
                ['Ad hoc payments',    generateAdHoc     ? 'Yes' : 'No'],
                ['Goals',              generateGoals     ? 'Yes' : 'No'],
                ['Coach insights',     generateCoachInsights ? 'Yes' : 'No'],
              ].map(([k, v]) => (
                <Box key={k}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" fontWeight={500}>{v}</Typography>
                </Box>
              ))}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={generateNow}
                  onChange={(e) => setGenerateNow(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Generate data immediately</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kick off data generation as soon as the account is created.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mt: 0.5 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {step > 0 && <Button onClick={handleBack} disabled={saving}>Back</Button>}
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>Next</Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving
              ? (generateNow ? 'Creating & generating…' : 'Creating…')
              : (generateNow ? 'Create & Generate' : 'Create Demo Account')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateDemoAccountDialog;
